import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { IconButton } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { AI_GENERATED, HUMAN_GENERATED } from 'constants/index';
import { isNil } from 'lodash';
import {
  studentAssignmentChatbotSendMessage,
  useStudentAssignmentChatbotGetMessages,
  useStudentAssignmentChatbotStatus
} from 'sdk';

import AssignmentChatbotQuery from 'pages/Students/Assignments/Overview/components/AssignmentChatBotQuery';
import AssignmentChatBotReply from 'pages/Students/Assignments/Overview/components/AssignmentChatBotReply';
import {
  CHATBOT_MESSAGES,
  CHATBOT_MESSAGE_STATUSES,
  CHATBOT_STATUSES
} from 'pages/Students/Assignments/Overview/constants';

import UserContext from 'components/UserContext';

import styles from './styles.module.scss';

const AssignmentChatBot = ({ assignment, onClose }) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [chatMessageHistory, setChatMessageHistory] = useState([]);

  const { user } = useContext(UserContext);

  const { data: status, refetch: refetchStatus } =
    useStudentAssignmentChatbotStatus({
      student: user.student_id,
      trackerInstanceSection: assignment.id
    });

  const {
    data: chatbotHistory,
    refetch: refetchChatbotHistory,
    isLoading: isLoadingChatbotHistory
  } = useStudentAssignmentChatbotGetMessages({
    studentId: user.student_id,
    trackerInstanceSectionId: assignment.id
  });

  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeChatbotCall = useCallback(async () => {
    await studentAssignmentChatbotSendMessage({
      studentId: user.student_id,
      trackerInstanceSectionId: assignment.id,
      message: ''
    });
  }, [user.student_id, assignment.id]);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessageHistory]);

  useEffect(() => {
    if (
      !isNil(status) &&
      status['status'] === CHATBOT_STATUSES.WAITING_FOR_INITIAL_MESSAGE
    ) {
      initializeChatbotCall();
    }
  }, [status, initializeChatbotCall]);

  useEffect(() => {
    if (!isNil(status) && status['status'] === CHATBOT_STATUSES.READY) {
      refetchChatbotHistory();
    }
  }, [status, refetchChatbotHistory]);

  useEffect(() => {
    if (
      !isNil(status) &&
      !isLoadingChatbotHistory &&
      chatbotHistory?.length < 2
    ) {
      setChatMessageHistory([
        {
          message_type: AI_GENERATED,
          text: CHATBOT_MESSAGES[status['status']]
        }
      ]);
    }
  }, [status, chatbotHistory, isLoadingChatbotHistory]);

  useEffect(() => {
    if (chatbotHistory && chatbotHistory?.length > 0) {
      setChatMessageHistory(chatbotHistory);
    }
  }, [chatbotHistory]);

  const shouldPoll = useMemo(() => {
    if (isLoadingChatbotHistory || chatMessageHistory?.length === 0)
      return false;
    const lastMessageStatus =
      chatMessageHistory[chatMessageHistory.length - 1].status;

    return lastMessageStatus === CHATBOT_MESSAGE_STATUSES.IN_PROGRESS;
  }, [chatMessageHistory, isLoadingChatbotHistory]);

  useEffect(() => {
    if (shouldPoll) {
      const interval = setInterval(() => refetchChatbotHistory(), 3000);

      return () => clearInterval(interval);
    }
  }, [shouldPoll, refetchChatbotHistory]);

  useEffect(() => {
    if (isLoadingChatbotHistory || chatMessageHistory?.length === 0) return;
    const lastMessageStatus =
      chatMessageHistory[chatMessageHistory.length - 1].status;

    if (lastMessageStatus === CHATBOT_MESSAGE_STATUSES.ERROR) {
      setErrorMessage('Oops, something went wrong. Please try again.');
    }
  }, [chatMessageHistory, isLoadingChatbotHistory]);

  const shouldPollInitialStatus = useMemo(() => {
    if (isNil(status)) return false;
    return (
      status.status !== CHATBOT_STATUSES.READY &&
      status.status !== CHATBOT_STATUSES.ERROR
    );
  }, [status]);

  useEffect(() => {
    if (shouldPollInitialStatus) {
      const interval = setInterval(() => {
        refetchStatus();
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [shouldPollInitialStatus, refetchStatus]);

  const addMessageToChatHistory = (message) => {
    setChatMessageHistory((history) => [
      ...history,
      {
        id: message.id ?? Date.now(),
        message_type: message.message_type,
        text: message.text
      }
    ]);
  };

  const sendMessageToChatbot = async ({ message }) => {
    setErrorMessage(null); // reset previous errors

    try {
      const { errors, success } = await studentAssignmentChatbotSendMessage({
        studentId: user.student_id,
        trackerInstanceSectionId: assignment.id,
        message
      });

      if (success) {
        refetchChatbotHistory();
      } else {
        setErrorMessage(errors.message);
      }
    } catch (err) {
      setErrorMessage('Oops, something went wrong. Please try again.');
    }
  };

  const handleOptionSelected = ({ option }) => {
    addMessageToChatHistory({
      id: null,
      text: option,
      message_type: HUMAN_GENERATED,
      status: CHATBOT_MESSAGE_STATUSES.IN_PROGRESS
    });

    sendMessageToChatbot({ message: option });
  };

  const questionToPromptMapper = useMemo(() => {
    const filteredMessages = chatMessageHistory.filter(
      (message) => message.message_type === HUMAN_GENERATED
    );

    const mapper = {};
    filteredMessages.forEach((message) => {
      mapper[message.id] = message.followup_prompts || [];
    });
    return mapper;
  }, [chatMessageHistory]);

  const lastQuestionPrompts = useMemo(() => {
    const filteredMessages = chatMessageHistory.filter(
      (message) =>
        message.message_type === HUMAN_GENERATED &&
        message.status !== CHATBOT_MESSAGE_STATUSES.ERROR
    );
    if (filteredMessages?.length === 0) return null;
    const lastMessage = filteredMessages[filteredMessages.length - 1];
    return lastMessage.followup_prompts || [];
  }, [chatMessageHistory]);

  return (
    <div className={styles.chatWindow}>
      <div className={styles.header}>
        <h2>Ember</h2>
        <IconButton
          size="small"
          className={styles.closeButton}
          onClick={onClose}
        >
          <span className={styles.closeIcon}>x</span>
        </IconButton>
      </div>

      {isNil(status) ? (
        <div className={styles.initializingContainer}>
          <CircularProgress size={24} />
        </div>
      ) : (
        <>
          <div className={styles.chatBody}>
            {chatMessageHistory.map((message, index) => (
              <div key={`message-${index}`} className={styles.messageWrapper}>
                {message.message_type === AI_GENERATED && (
                  <>
                    <AssignmentChatBotReply
                      message={message}
                      status={status['status']}
                      hideLoader={chatMessageHistory.length > 1}
                      hideLoadingText={chatMessageHistory.length > 1}
                    />
                    {index === chatMessageHistory.length - 1 && (
                      <AssignmentChatbotQuery
                        options={questionToPromptMapper[message['question_id']]}
                        showOptions={true}
                        onOptionSelect={handleOptionSelected}
                      />
                    )}
                  </>
                )}
                {message.message_type === HUMAN_GENERATED && (
                  <AssignmentChatbotQuery message={message.text} />
                )}
              </div>
            ))}

            {shouldPoll && (
              <AssignmentChatBotReply
                showActions={false}
                isLoading={true}
                status={status['status']}
                hideLoader={false}
                hideLoadingText={chatMessageHistory?.length > 1}
              />
            )}

            {errorMessage && (
              <>
                <AssignmentChatBotReply
                  message={{ text: errorMessage }}
                  showActions={false}
                  isError={true}
                  status={status['status']}
                />
                {!isNil(lastQuestionPrompts) && (
                  <AssignmentChatbotQuery
                    options={lastQuestionPrompts}
                    showOptions={true}
                    onOptionSelect={handleOptionSelected}
                  />
                )}
              </>
            )}
            <div ref={chatEndRef} />
          </div>
        </>
      )}
    </div>
  );
};

export default AssignmentChatBot;
